import React, { useEffect, useMemo, useState } from 'react';
import { Search } from '@fabric-ui/icons';
import { flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { capitalizeSentence } from '@fabric-ui/utils';
import { Avatar, Box, ClickAwayListener, Fade, TextField, Typography, useAutocomplete } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { EnabledApp, useAvContext } from './context';

type TypeApp = Record<string, any>;
interface SpotlightSearchLogicProps<TApp = EnabledApp, TMenuItem = TypeApp> {
  apps: TApp[];
  showSpotlight: boolean;
  setShowSpotlight: (showSpotlight: any) => void;
  appsMenuItems: Record<string, TMenuItem[]>;
  appPaths: Record<string, string>;
  publicApps: string[];
  pathFeatureFlag: Record<string, string>;
}

function SpotlightSearchLogic<TApp = TypeApp, TMenuItem = TypeApp>({
  apps,
  showSpotlight,
  setShowSpotlight,
  appsMenuItems,
  appPaths,
  publicApps,
  pathFeatureFlag,
}: SpotlightSearchLogicProps<TApp, TMenuItem>) {
  const {
    getPathName,
    accountData,
    featureFlags,
    setSelectedApp,
    userPermissions: { hasAllowedPermission, isInternalRole, isBackOfficeAvailable },
    user,
    setAccountId,
  } = useAvContext();
  const navigate = useNavigate();

  const isAllowedPath = (path, isPathBackOffice, permissionAppName, permission) =>
    (hasAllowedPermission({ path, app: permissionAppName, permission }) &&
      (!pathFeatureFlag[path] || featureFlags[pathFeatureFlag[path]]) &&
      !isPathBackOffice) ||
    (isBackOfficeAvailable && isPathBackOffice);
  const ACCOUNT_GROUP = 'Accounts';

  const pages = useMemo(() => {
    const allApps = [...publicApps, ...apps.map((app: any) => app.id)];

    return [
      ...Object.keys(appsMenuItems)
        .reduce(
          (acc, k) => [
            ...acc,
            ...appsMenuItems[k]
              .map(i => ({ ...i, appUrl: k }))
              .flatMap((r: any) =>
                r.subMenu
                  .flatMap(s => (s.subMenu ? s.subMenu.map(subMenu => ({ ...subMenu, parentPath: s.path })) : s))
                  .filter(subMenuItem => isAllowedPath(subMenuItem.path, subMenuItem.isBackOffice, k, subMenuItem.permission))
                  .map(s => ({
                    ...s,
                    appPath: r.appUrl,
                    parent: r.name,
                    path: s.parentPath ? `${s.parentPath}/${s.path}` : s.path,
                  }))
              ),
          ],
          [] as any[]
        )
        .map(r => ({
          group: r.appPath,
          value: `${r.appPath}/${r.path}`,
          pagePath: r.path,
          title: r.name === 'Settings' ? capitalizeSentence(`${r.parent.toLowerCase()} ${r.name}`) : capitalizeSentence(`${r.name}`),
          icon: r.icon,
        }))
        .filter(o => allApps.includes(o.group)),
      ...(isInternalRole
        ? (user.accounts || []).map(account => ({
            value: account.accountId,
            icon: <Avatar src={`/account-logo/${account.accountId}.png`} alt={account.accountName} sx={{ width: 30, height: 30 }} />,
            group: ACCOUNT_GROUP,
            pagePath: account.accountId,
            title: account.accountName,
          }))
        : []),
    ];
  }, [apps]);

  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
    id: 'spotlight',
    options: pages,
    getOptionLabel: option => option.title,
    getOptionKey: ({ group, value }) => group + value,
    groupBy: o => o.group,
    clearOnBlur: true,
    clearOnEscape: true,
    onChange: (event, option) => {
      event.stopPropagation();
      if (option) {
        if (option.group === ACCOUNT_GROUP) {
          if (option.value !== user.accountId) {
            setAccountId(option.value);
            setSelectedApp(appPaths[accountData.apps[0]?.name] || '');
            navigate({ to: `/${option.value}` });
          }
        } else {
          navigate(getPathName(option?.pagePath, '', option?.group));
        }
        setShowSpotlight(false);
      }
    },
  });

  const isRenderOptions = (getInputProps().value as string)?.length > 0;

  return (
    <ClickAwayListener onClickAway={() => setShowSpotlight(false)}>
      <Fade in={showSpotlight}>
        <Box
          sx={{
            position: 'absolute',
            right: 'calc(50vw - 250px)',
            top: 200,
            width: '500px',
            background: getCssVarColorWithOpacity('var(--color-surface-immutable-darkGrey)', 0.9),
            p: 3,
            borderRadius: 2,
            zIndex: theme => theme.zIndex.tooltip,
            color: 'var(--color-content-immutable-white)',
            input: {
              fontSize: 26,
              color: 'var(--color-content-immutable-white)',
              '&.MuiInputBase-input::placeholder': { color: 'var(--color-content-immutable-lightGrey)' },
            },
            '.MuiOutlinedInput-root': { background: 'transparent' },
            'fieldset.MuiOutlinedInput-notchedOutline': { border: 'none' },
            ul: { listStyleType: 'none', paddingInlineStart: 1 },
          }}
          {...getRootProps()}>
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            <Search />
            {/* @ts-ignore */}
            <TextField
              {...getInputProps()}
              autoFocus
              onBlur={() => setShowSpotlight(false)}
              placeholder="Search Page"
              onFocusCapture={e => (e.target as HTMLInputElement).select()}
            />
          </Box>
          <Box component="ul" {...getListboxProps()} sx={{ maxHeight: 500, overflow: 'auto', my: '14px' }}>
            {groupedOptions.length > 0 &&
              isRenderOptions &&
              groupedOptions.map(group => (
                <Box key={group.group} component="li">
                  <Typography variant="h5" sx={{ my: 1 }}>
                    {capitalizeSentence(group.group)}
                  </Typography>
                  <Box component="ul" sx={{ ...flex.col, gap: 1 }}>
                    {group.options.map((option, index) => (
                      <Box
                        component="li"
                        sx={{
                          ...flex.itemsCenter,
                          gap: 2,
                          p: 1,
                          borderRadius: 2,
                          cursor: 'pointer',
                          '&.Mui-focused, &:active': {
                            backgroundColor: 'var(--color-surface-interactive-primary-default)',
                            color: 'var(--color-content-inverted-base-primary)',
                          },
                        }}
                        {...getOptionProps({ option, index: index + group.index })}
                        key={`spot-${option.value}`}>
                        {option.icon}
                        {option.title}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </Fade>
    </ClickAwayListener>
  );
}

type SpotlightSearchProps<TApp, TMenuItem> = Omit<SpotlightSearchLogicProps, 'showSpotlight' | 'setShowSpotlight'>;
export function SpotlightSearch<TApp, TMenuItem>({
  apps,
  appPaths,
  publicApps,
  pathFeatureFlag,
  appsMenuItems,
}: SpotlightSearchProps<TApp, TMenuItem>) {
  const [showSpotlight, setShowSpotlight] = useState(false);
  const onKeyDown = e => {
    if (e.code === 'KeyK' && (e.metaKey || e.ctrlKey)) {
      setShowSpotlight(true);
      e.preventDefault();
    } else if (e.code === 'Escape') {
      setShowSpotlight(false);
    }
  };
  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  return (
    showSpotlight && (
      <SpotlightSearchLogic
        apps={apps}
        showSpotlight={showSpotlight}
        setShowSpotlight={setShowSpotlight}
        appPaths={appPaths}
        appsMenuItems={appsMenuItems}
        publicApps={publicApps}
        pathFeatureFlag={pathFeatureFlag}
      />
    )
  );
}
